import { ref, toRefs, watch, computed } from '@vue/composition-api';
import { Message } from 'byted-vui';
import { FULL_TIME_FORMAT } from '@ad/tt_ads_common_enums';
import { i18n } from './../../../../i18n';
import { useRequest, useGetDaylightSavingTimeApi } from '@tt4b/ttam-hooks';
import { dayjs } from '@tt4b/ttam-utils';

const STD = 'std_time_zone_data';
const DST = 'dst_time_zone_data';

const timeAbbMap = {
  cst: {
    'Central Standard Time': 'cst_central_standard_time',
    'Cuba Standard Time': 'cst_cuba_standard_time',
    'China Standard Time': 'cst_china_standard_time',
  },

  amt: {
    'Armenia Time': 'amt_armenia_time',
    'Amazon Time': 'amt_amazon_time',
  },

  cdt: {
    'Central Daylight Time': 'cdt_Central_Daylight_Time',
    'Cuba Daylight Time': 'cdt_Cuba_Daylight_Time',
  },

  ist: {
    'Irish Standard Time': 'ist_Irish_Standard_Time',
    'Israel Standard Time': 'ist_Israel_Standard_Time',
    'India Standard Time': 'ist_India_Standard_Time',
  },

  bst: {
    'Bangladesh Standard Time': 'bst_Bangladesh_Standard_Time',
    'Bougainville Standard Time': 'bst_Bougainville_Standard_Time',
  },

  ast: {
    'Atlantic Standard Time': 'ast_Atlantic_Standard_Time',
    'Arabia Standard Time': 'ast_Arabia_Standard_Time',
  },
};

const handleTimeAbb = (timeAbb, timeName): string => timeAbbMap[timeAbb]?.[timeName] || timeAbb;

type TableTimeData = {
  locations: string;
  startTime: string;
  endTime: string;
  timeZoneStartDesc: string;
  timeZoneStartName: string;
  timeZoneEndDesc: string;
  timeZoneEndName: string;
  showTooltip: boolean;
  isTimeChange: boolean;
};
interface DaylightTime {
  area_list: [];
  dst_time_data_list: [];
}

export enum ELeType {
  /**
   * 区间投放-起始时间、结束时间都在夏令时时区
   */
  isDurationStartTimeDSTAndEndTimeDST,
  /**
   * 区间投放-起始时间在冬令时时区、结束时间在夏令时时区
   */
  isDurationStartTimeSTDAndEndTimeDST,
  /**
   * 区间投放-起始时间在夏令时时区、结束时间在冬令时时区
   */
  isDurationStartTimeDSTAndEndTimeSTD,
  /**
   * 区间投放-起始时间、结束时间都在冬令时时区
   */
  isDurationStartTimeSTDAndEndTimeSTD,
  /**
   * 持续投放-起始时间在夏令时时区
   */
  isContinuouslyStartTimeDST,
  /**
   * 持续投放-起始时间在冬令时时区
   */
  isContinuouslyStartTimeSTD,
}

export function getELeType(isDuration, startUTCTime, endUTCTime, startDSTTime, endDSTTime): ELeType {
  // 区间投放
  if (isDuration) {
    // 起始点完全在夏令时区间内
    if (
      startUTCTime >= startDSTTime &&
      startUTCTime < endDSTTime &&
      endUTCTime >= startDSTTime &&
      endUTCTime < endDSTTime
    ) {
      return ELeType.isDurationStartTimeDSTAndEndTimeDST;
      // 标准时区->夏令时跳变
    } else if (startUTCTime < startDSTTime && endUTCTime >= startDSTTime && endUTCTime < endDSTTime) {
      return ELeType.isDurationStartTimeSTDAndEndTimeDST;
      // 夏令时->标准时区跳变
    } else if (startUTCTime >= startDSTTime && startUTCTime < endDSTTime && endUTCTime >= endDSTTime) {
      return ELeType.isDurationStartTimeDSTAndEndTimeSTD;
      // 起始点完全不在夏令时区间
    } else {
      return ELeType.isDurationStartTimeSTDAndEndTimeSTD;
    }
    // 持续投放
  } else {
    // 在夏令时区间内
    if (startUTCTime >= startDSTTime && startUTCTime < endDSTTime) {
      return ELeType.isContinuouslyStartTimeDST;
    } else {
      return ELeType.isContinuouslyStartTimeSTD;
    }
  }
}
// TODO: 增加 Zipcode 如何支持
export function useTimeConverter(props) {
  const { startTime, endTime, isDuration, locationData } = toRefs(props);
  const requestParams = computed(() => {
    const { countryIds, provinceIds, cityIds, particleLocations } = locationData.value;

    return {
      year: startTime.value.split('-')[0],
      country_ids: countryIds,
      province_ids: provinceIds,
      city_ids: cityIds,
      // 市级以下的地区
      particle_location_ids: (particleLocations || []).filter(parId => {
        for (const coId of countryIds) {
          if (coId === parId) {
            return false;
          }
        }
        for (const proId of provinceIds) {
          if (proId === parId) {
            return false;
          }
        }
        for (const ciId of cityIds) {
          if (ciId === parId) {
            return false;
          }
        }

        return true;
      }),
    };
  });
  const { data, isLoading, error } = useRequest(useGetDaylightSavingTimeApi, requestParams);
  watch(error, () => {
    if (error.value) {
      Message.error(`${i18n.t('adgroup_scheduling_timezone_table_desc')}`);
    }
  });
  const timeTableData = ref<TableTimeData[]>([]);

  const setTimezoneDesc = (startTimezoneMsg, endTimezoneMsg) => [
    `(UTC${startTimezoneMsg.offset})`,
    handleTimeAbb(startTimezoneMsg.zone_abb.toLowerCase(), startTimezoneMsg.zone_name),
    `(UTC${endTimezoneMsg.offset})`,
    handleTimeAbb(endTimezoneMsg.zone_abb.toLowerCase(), endTimezoneMsg.zone_name),
  ];
  // 获取账户时区(不支持夏令时)相对于UTC+0时区的偏移量（分钟）
  const timezoneOffect = (tStr: string): number => {
    const timezHourNum = Number(tStr.slice(3).slice(0, 3) || 0);
    const timezMinNum = Number(tStr.slice(7) || 0);
    return timezHourNum >= 0 ? timezHourNum * 60 + timezMinNum : timezHourNum * 60 - timezMinNum;
  };
  const constructTimeTableItem = (listItem, timeListKey) => {
    const ele = {
      locations: '',
      startTime: '',
      endTime: '',
      timeZoneStartDesc: '',
      timeZoneStartName: '',
      timeZoneEndDesc: '',
      timeZoneEndName: '',
      showTooltip: false,
      isTimeChange: false,
      aggregatedRow: 0,
      hasTime: true,
    };
    ele.locations = listItem.area_list.reduce(
      (pre, cur, i) => (i === 0 ? cur.area_name : `${pre}, ${cur.area_name}`),
      '',
    );
    // 第三方api没获取到时区信息
    if (timeListKey === '') {
      ele.hasTime = false;
      ele.aggregatedRow = 1;

      return ele;
    }
    if (!Number(timeListKey)) {
      ele.aggregatedRow = listItem.dst_time_data_list.length;
    }

    const startUTCTime = dayjs(
      (dayjs as any)
        .tz(startTime.value, 'Etc/GMT-0') // 1. 把时间当做UTC+0的时间
        .add(-timezoneOffect(props.timezoneStr), 'minute') // 2. 减去账户时区相对于UTC+0的分钟偏移量
        .format(FULL_TIME_FORMAT),
    ).valueOf();
    const endUTCTime = dayjs(
      (dayjs as any)
        .tz(endTime.value, 'Etc/GMT-0') // 1. 把时间当做UTC+0的时间
        .add(-timezoneOffect(props.timezoneStr), 'minute') // 2. 减去账户时区相对于UTC+0的分钟偏移量
        .format(FULL_TIME_FORMAT),
    ).valueOf();

    const setTimeInfo = (isTimeChange, startTimeOffset, endTimeOffset, startTimezone, endTimezone) => {
      ele.isTimeChange = isTimeChange;
      ele.startTime = dayjs(startUTCTime + startTimeOffset * 1000)
        .format(FULL_TIME_FORMAT)
        .slice(0, -3);
      ele.endTime = dayjs(endUTCTime + endTimeOffset * 1000)
        .format(FULL_TIME_FORMAT)
        .slice(0, -3);
      [ele.timeZoneStartDesc, ele.timeZoneStartName, ele.timeZoneEndDesc, ele.timeZoneEndName] = setTimezoneDesc(
        listItem.dst_time_data_list[timeListKey][startTimezone],
        listItem.dst_time_data_list[timeListKey][endTimezone],
      );
    };

    /** 夏令时的偏移时间 */
    const dstOffset = listItem.dst_time_data_list[timeListKey].dst_time_zone_data.zone_total_offset;
    /** 标准时的偏移时间 */
    const stdOffset = listItem.dst_time_data_list[timeListKey].std_time_zone_data.zone_total_offset;
    // 如果当前地区无夏令时
    if (!listItem.dst_time_data_list[timeListKey].time_changes) {
      setTimeInfo(false, stdOffset, stdOffset, STD, STD);

      return ele;
    }
    /** 夏令时开始的UTC时间 */
    const startDSTTime = dayjs(listItem.dst_time_data_list[timeListKey].time_changes[0].utc_time).valueOf();
    /** 夏令时结束的UTC时间*/
    const endDSTTime = dayjs(listItem.dst_time_data_list[timeListKey].time_changes[1].utc_time).valueOf();
    const ELeHandleFun = {
      [ELeType.isDurationStartTimeDSTAndEndTimeDST]: () => setTimeInfo(false, dstOffset, dstOffset, DST, DST),
      [ELeType.isDurationStartTimeSTDAndEndTimeDST]: () => setTimeInfo(true, stdOffset, dstOffset, STD, DST),
      [ELeType.isDurationStartTimeDSTAndEndTimeSTD]: () => setTimeInfo(true, dstOffset, stdOffset, DST, STD),
      [ELeType.isDurationStartTimeSTDAndEndTimeSTD]: () => setTimeInfo(false, stdOffset, stdOffset, STD, STD),
      [ELeType.isContinuouslyStartTimeDST]: () => setTimeInfo(false, dstOffset, dstOffset, DST, DST),
      [ELeType.isContinuouslyStartTimeSTD]: () => setTimeInfo(false, stdOffset, stdOffset, STD, STD),
    };

    ELeHandleFun[getELeType(isDuration.value, startUTCTime, endUTCTime, startDSTTime, endDSTTime)]();

    return ele;
  };
  watch(
    [startTime, endTime, data, isDuration],

    () => {
      timeTableData.value = [];
      if (data.value) {
        Object.keys(data.value).forEach(dataKey => {
          const list = data.value[dataKey] as DaylightTime[];
          for (const listItem of list) {
            // 第三方api没获取到时区信息
            if (!listItem.dst_time_data_list.length) {
              timeTableData.value.push(constructTimeTableItem(listItem, ''));
            } else {
              listItem.dst_time_data_list.forEach((_, timeListKey) => {
                timeTableData.value.push(constructTimeTableItem(listItem, timeListKey));
              });
            }
          }
        });
      }
    },
    { immediate: true },
  );

  return {
    timeTableData,
    isLoading,
  };
}
